/**
 * RichTextEditor — TipTap-based редактор с toolbar для жирного/курсива/
 * подчёркнутого/ссылки. Output — HTML, совместимый с TG parse_mode=HTML.
 *
 * Используется в чате (composer) и рассылках (broadcast editor).
 */
import { useEditor, EditorContent, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import { Button, Space, Input, Modal, message, Typography, Alert } from "antd";
import {
  BoldOutlined, ItalicOutlined, StrikethroughOutlined,
  LinkOutlined, CodeOutlined, ThunderboltOutlined,
} from "@ant-design/icons";
import { useEffect, useState } from "react";
import "./RichTextEditor.css";
import { API_URL } from "../dataProvider";
import { EmojiPicker } from "./EmojiPicker";

interface Props {
  initialHtml?: string;
  placeholder?: string;
  onChange?: (html: string, isEmpty: boolean) => void;
  minHeight?: number;
  enableProofread?: boolean;   // показать кнопку ✨ AI-проверка
  /** Если true — редактор только для чтения (например, юзер заблокировал бота). */
  disabled?: boolean;
  /**
   * Колбэк с TipTap-инстансом редактора. Вызывается один раз когда editor
   * создан, и `null` когда unmount. Через него родитель может вызвать
   * `editor.chain().focus().insertContent(...)` — например для вставки
   * переменной из выпадашки ChatComposer.
   */
  onEditorReady?: (editor: Editor | null) => void;
}

interface ProofIssue {
  offset: number;
  length: number;
  message: string;
  original?: string;
  replacement?: string | null;
  issueType?: string;
}

export const RichTextEditor = ({
  initialHtml = "",
  placeholder = "Напишите сообщение…",
  onChange,
  minHeight = 80,
  enableProofread = false,
  disabled = false,
  onEditorReady,
}: Props) => {
  const [linkModal, setLinkModal] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");

  const [proofBusy, setProofBusy] = useState(false);
  const [proofModalOpen, setProofModalOpen] = useState(false);
  const [proofOriginal, setProofOriginal] = useState("");
  const [proofCorrected, setProofCorrected] = useState("");
  const [proofIssues, setProofIssues] = useState<ProofIssue[]>([]);

  const editor = useEditor({
    editable: !disabled,
    extensions: [
      StarterKit.configure({
        heading: false, blockquote: false, codeBlock: false, horizontalRule: false,
        listItem: false, bulletList: false, orderedList: false,
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { rel: "noopener", target: "_blank" },
      }),
      Placeholder.configure({ placeholder }),
    ],
    content: initialHtml,
    onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML(), editor.isEmpty);
    },
  });

  // Динамически переключаем editable когда меняется prop disabled (например
  // юзер заблокировал бота во время уже открытой сессии админа).
  useEffect(() => {
    if (editor) editor.setEditable(!disabled);
  }, [editor, disabled]);

  // Дать родителю инстанс редактора (или null когда unmount) — чтобы он
  // мог вставлять контент через `editor.chain().focus().insertContent(...)`.
  useEffect(() => {
    onEditorReady?.(editor ?? null);
    return () => onEditorReady?.(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  if (!editor) return null;

  const openLinkModal = () => {
    const prev = editor.getAttributes("link").href;
    setLinkUrl(prev || "");
    setLinkModal(true);
  };
  const applyLink = () => {
    const url = linkUrl.trim();
    if (!url) {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
    }
    setLinkModal(false);
  };

  const runProofread = async () => {
    const plain = editor.getText().trim();
    if (!plain) {
      message.warning("Текст пустой — нечего проверять");
      return;
    }
    setProofBusy(true);
    try {
      const r = await fetch(`${API_URL}/admin/chat/proofread`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: plain, lang: "ru-RU" }),
      });
      if (!r.ok) {
        const t = await r.text().catch(() => r.statusText);
        message.error(`Ошибка ${r.status}: ${t.slice(0, 200)}`);
        return;
      }
      const d = await r.json();
      setProofOriginal(d.original || plain);
      setProofCorrected(d.corrected || plain);
      setProofIssues(d.issues || []);
      setProofModalOpen(true);
      if ((d.changes ?? 0) === 0 && (d.issues || []).length === 0) {
        message.success("Ошибок не найдено ✓");
        setProofModalOpen(false);
      }
    } catch (e) {
      message.error(`Сетевая ошибка: ${(e as Error).message}`);
    } finally {
      setProofBusy(false);
    }
  };

  const applyProofread = () => {
    // Превращаем plain-text (с \n\n как разделителем абзацев) обратно в HTML
    // с <p> и <br> — без потери base-форматирования. Bold/italic потеряются
    // (это известный trade-off для MVP — для сохранения нужен diff-merge).
    const html = proofCorrected
      .split(/\n\n+/)
      .map((p) => `<p>${p.replace(/\n/g, "<br>")}</p>`)
      .join("");
    editor.commands.setContent(html);
    setProofModalOpen(false);
    message.success("Текст исправлен ✓");
  };

  const btn = (
    icon: React.ReactNode, on: boolean, action: () => void, title: string,
  ) => (
    <Button size="small" type={on ? "primary" : "default"} icon={icon}
            onClick={action} title={title} />
  );

  return (
    <div className="bemli-rich-editor"
         style={{ border: "1px solid #d9d9d9", borderRadius: 6,
                  background: "#fff",
                  // дает hard minHeight даже если parent не flex
                  minHeight: minHeight + 48 /* + toolbar */ }}>
      <Space size={4} style={{ padding: "6px 6px 6px 8px",
                                borderBottom: "1px solid #f0f0f0",
                                flex: "0 0 auto",
                                justifyContent: "space-between",
                                width: "100%" }}>
        <Space size={4}>
          {btn(<BoldOutlined />, editor.isActive("bold"),
               () => editor.chain().focus().toggleBold().run(), "Жирный (Ctrl+B)")}
          {btn(<ItalicOutlined />, editor.isActive("italic"),
               () => editor.chain().focus().toggleItalic().run(), "Курсив (Ctrl+I)")}
          {btn(<StrikethroughOutlined />, editor.isActive("strike"),
               () => editor.chain().focus().toggleStrike().run(), "Зачёркнутый")}
          {btn(<CodeOutlined />, editor.isActive("code"),
               () => editor.chain().focus().toggleCode().run(), "Моноширинный")}
          {btn(<LinkOutlined />, editor.isActive("link"),
               openLinkModal, "Ссылка")}
          <EmojiPicker
            onSelect={(emoji) => {
              // Вставляем emoji в текущую позицию курсора.
              editor.chain().focus().insertContent(emoji).run();
            }}
          />
        </Space>
        {enableProofread && (
          <Button size="small" icon={<ThunderboltOutlined />}
                  loading={proofBusy} onClick={runProofread}
                  title="AI-проверка орфографии, пунктуации и грамматики">
            ✨ Проверить текст
          </Button>
        )}
      </Space>
      <EditorContent
        editor={editor}
        className="bemli-rich-editor-content"
      />
      <Modal
        title="Вставить ссылку" open={linkModal}
        onCancel={() => setLinkModal(false)}
        onOk={applyLink} okText="Применить"
      >
        <Input
          placeholder="https://..." value={linkUrl}
          onChange={(e) => setLinkUrl(e.target.value)}
          onPressEnter={applyLink}
        />
      </Modal>

      <Modal
        title="✨ Проверка орфографии, пунктуации и грамматики"
        open={proofModalOpen}
        onCancel={() => setProofModalOpen(false)}
        width={760}
        footer={[
          <Button key="cancel" onClick={() => setProofModalOpen(false)}>
            Не применять
          </Button>,
          <Button key="apply" type="primary" onClick={applyProofread}
                  disabled={proofCorrected === proofOriginal}>
            Применить исправления
          </Button>,
        ]}
      >
        {proofCorrected === proofOriginal ? (
          <Alert type="success" showIcon
            message="AI считает что текст в порядке — изменений не предлагает." />
        ) : (
          <>
            <Alert type="info" showIcon style={{ marginBottom: 12 }}
              message={
                <span>
                  AI исправил орфографию, пунктуацию, регистр и расставил абзацы.{" "}
                  <strong>После применения форматирование (жирный / курсив / ссылки) сбросится</strong>{" "}
                  — добавьте его заново при необходимости.
                </span>
              }
            />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr",
                          gap: 16, marginBottom: 16 }}>
              <div>
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                  Было ({proofOriginal.length} симв.):
                </Typography.Text>
                <pre style={{ whiteSpace: "pre-wrap", background: "#fff7e6",
                              padding: 10, borderRadius: 4, fontSize: 13,
                              maxHeight: 360, overflowY: "auto",
                              border: "1px solid #ffd591" }}>
                  {proofOriginal}
                </pre>
              </div>
              <div>
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                  Стало ({proofCorrected.length} симв.):
                </Typography.Text>
                <pre style={{ whiteSpace: "pre-wrap", background: "#f6ffed",
                              padding: 10, borderRadius: 4, fontSize: 13,
                              maxHeight: 360, overflowY: "auto",
                              border: "1px solid #b7eb8f" }}>
                  {proofCorrected}
                </pre>
              </div>
            </div>
            {proofIssues.length > 0 && (
              <details>
                <summary style={{ cursor: "pointer", fontSize: 12, color: "#888" }}>
                  Детали ({proofIssues.length}):
                </summary>
                <ul style={{ fontSize: 12, marginTop: 8, paddingLeft: 20 }}>
                  {proofIssues.slice(0, 30).map((iss, i) => (
                    <li key={i} style={{ marginBottom: 4 }}>
                      <Typography.Text type="secondary">{iss.message}</Typography.Text>
                      {iss.original && (
                        <span>
                          {" — "}<code>{iss.original}</code>
                          {iss.replacement && <> → <code>{iss.replacement}</code></>}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </>
        )}
      </Modal>
    </div>
  );
};
